import { Chart, Plugin, ChartEvent } from 'chart.js';
import { BarChartOptionsWithLabelClick } from '../chart-types/bar-chart-options-with-label-click';
import { findMatchingXLabels, getXCategoryScale, normalizeLineHeight, resolveXLabelLayout } from '../chart-utils/x-label-geometry';

/**
 * Local runtime state stored on the chart instance by the click plugin.
 */
type ClickStateChart = Chart<'bar', number[], string> & {
    _xLabelClickHoverIndex?: number | null;
};

/**
 * Plugin is active only when explicit opt-in and click callback are provided.
 */
function isPluginEnabled(chart: Chart<'bar', number[], string>): boolean {
    const options = chart.options as BarChartOptionsWithLabelClick;
    return options.plugins?.xLabelClick?.enabled === true && typeof options.onLabelClick === 'function';
}

/**
 * Makes x-axis labels clickable for bar charts.
 *
 * Behavior:
 * - `mousemove`: resolves hovered label and updates pointer/underline state.
 * - `click`: invokes `onLabelClick(index, label)` when a label is hit.
 * - `afterDraw`: renders a subtle blue underline under the hovered label.
 */
export const xLabelClickPlugin: Plugin<'bar'> = {
    id: 'xLabelClick',

    afterDraw(chart: Chart<'bar', number[], string>) {
        if (!isPluginEnabled(chart)) {
            return;
        }

        const clickChart = chart as ClickStateChart;
        const hoveredIndex = clickChart._xLabelClickHoverIndex;
        if (hoveredIndex == null) {
            return;
        }

        const xScale = getXCategoryScale(chart);
        if (!xScale) return;

        const layout = resolveXLabelLayout(chart, hoveredIndex, xScale);
        if (!layout) return;

        const ctx = chart.ctx;
        const defaultFont = Chart.defaults.font;
        const tickFont = xScale.options?.ticks?.font;
        const fontSize = tickFont?.size ?? defaultFont.size ?? 12;
        const lineHeight = normalizeLineHeight(tickFont?.lineHeight ?? defaultFont.lineHeight ?? 1.2, fontSize);
        const textWidth = layout.localXMax - layout.localXMin;
        const itemLineHeight = layout.lineHeight || lineHeight;
        const textHeight = Math.max(layout.lines.length * itemLineHeight, itemLineHeight);

        ctx.save();
        ctx.translate(layout.tx, layout.ty);
        ctx.rotate(layout.rotation);

        ctx.strokeStyle = 'rgba(0, 90, 130, 0.95)';
        ctx.lineWidth = 1.5;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(-Math.max(18, textWidth / 2), layout.textOffset + textHeight / 2 + 6);
        ctx.lineTo(Math.max(18, textWidth / 2), layout.textOffset + textHeight / 2 + 6);
        ctx.stroke();

        ctx.restore();
    },

    afterEvent(chart: Chart<'bar', number[], string>, args: { event: ChartEvent }) {
        const clickChart = chart as ClickStateChart;
        const event = args.event;
        const options = chart.options as BarChartOptionsWithLabelClick;

        if (!isPluginEnabled(chart)) {
            if (clickChart._xLabelClickHoverIndex != null) {
                clickChart._xLabelClickHoverIndex = null;
                chart.draw();
            }

            if (chart.canvas.style.cursor !== 'default') {
                chart.canvas.style.cursor = 'default';
            }
            return;
        }

        if (event.type === 'mouseout') {
            if (clickChart._xLabelClickHoverIndex != null) {
                clickChart._xLabelClickHoverIndex = null;
                chart.draw();
            }

            if (chart.canvas.style.cursor !== 'default') {
                chart.canvas.style.cursor = 'default';
            }
            return;
        }

        if (event.x == null || event.y == null) return;

        const matches = findMatchingXLabels(chart, event.x, event.y);
        const bestMatch = matches.length > 0
            ? matches.reduce((best, current) => current.score < best.score ? current : best)
            : null;

        if (event.type === 'mousemove') {
            const hoveredIndex = bestMatch?.index ?? null;
            if (clickChart._xLabelClickHoverIndex !== hoveredIndex) {
                clickChart._xLabelClickHoverIndex = hoveredIndex;
                chart.draw();
            }

            if (hoveredIndex != null) {
                chart.canvas.style.cursor = 'pointer';
            } else {
                const activeElements = chart.getElementsAtEventForMode(
                    event.native as Event,
                    'nearest',
                    { intersect: true },
                    false
                );
                chart.canvas.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
            }
            return;
        }

        if (event.type === 'click' && bestMatch) {
            options.onLabelClick?.(bestMatch.index, bestMatch.label);
        }
    }
};

export default xLabelClickPlugin;
